/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

// i18n wird dynamisch importiert um zirkuläre Abhängigkeiten zu vermeiden

export type SupportedLanguage = "de" | "en";

export class LanguageManager {
  private static readonly COOKIE_NAME = "preferredLanguage";
  private static readonly STORAGE_KEY = "preferredLanguage";

  static readonly SUPPORTED_LANGUAGES: SupportedLanguage[] = ["de", "en"];

  private static getCookieDomain(): string {
    const hostname = window.location.hostname;

    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "";
    }

    const cookieDomain = import.meta.env.VITE_COOKIE_DOMAIN;
    if (cookieDomain) {
      return cookieDomain;
    }

    return "";
  }

  static setLanguage(language: SupportedLanguage): void {
    console.log(`[LanguageManager] Setting language to: ${language}`);

    // Cookie setzen für Subdomain-übergreifende Synchronisation
    const cookieDomain = this.getCookieDomain();
    const cookieString = cookieDomain
      ? `${this.COOKIE_NAME}=${language}; domain=${cookieDomain}; path=/; max-age=31536000`
      : `${this.COOKIE_NAME}=${language}; path=/; max-age=31536000`;

    document.cookie = cookieString;
    console.log(`[LanguageManager] Cookie set: ${cookieString}`);

    // localStorage als lokaler Cache
    localStorage.setItem(this.STORAGE_KEY, language);
    console.log(`[LanguageManager] localStorage set: ${language}`);

    // Dispatch custom event for global language change
    window.dispatchEvent(
      new CustomEvent("languageChanged", { detail: { language } })
    );
  }

  static getLanguage(): SupportedLanguage {
    console.log(`[LanguageManager] Getting language...`);

    // 1. URL-Parameter prüfen (höchste Priorität)
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get("lang") as SupportedLanguage;
    if (urlLang && this.SUPPORTED_LANGUAGES.includes(urlLang)) {
      console.log(`[LanguageManager] Found URL language: ${urlLang}`);
      this.setLanguage(urlLang);
      return urlLang;
    }

    // 2. Cookie prüfen (für Subdomain-Synchronisation)
    const cookieLang = this.getCookieValue(
      this.COOKIE_NAME
    ) as SupportedLanguage;
    if (cookieLang && this.SUPPORTED_LANGUAGES.includes(cookieLang)) {
      console.log(`[LanguageManager] Found cookie language: ${cookieLang}`);
      return cookieLang;
    }

    // 3. localStorage als Fallback
    const storageLang = localStorage.getItem(
      this.STORAGE_KEY
    ) as SupportedLanguage;
    if (storageLang && this.SUPPORTED_LANGUAGES.includes(storageLang)) {
      console.log(
        `[LanguageManager] Found localStorage language: ${storageLang}`
      );
      return storageLang;
    }

    // 4. Browser-Sprache prüfen
    const browserLang = navigator.language.split("-")[0] as SupportedLanguage;
    if (this.SUPPORTED_LANGUAGES.includes(browserLang)) {
      console.log(`[LanguageManager] Found browser language: ${browserLang}`);
      return browserLang;
    }

    // 5. Standard-Sprache
    console.log(`[LanguageManager] Using default language: de`);
    return "de";
  }

  static getCurrentLanguage(): SupportedLanguage {
    // Fallback auf localStorage wenn i18n noch nicht initialisiert ist
    const storageLang = localStorage.getItem(
      this.STORAGE_KEY
    ) as SupportedLanguage;
    return storageLang && this.SUPPORTED_LANGUAGES.includes(storageLang)
      ? storageLang
      : "de";
  }

  static getLanguageDisplayName(language: SupportedLanguage): string {
    const displayNames: Record<SupportedLanguage, string> = {
      de: "Deutsch",
      en: "English",
    };
    return displayNames[language];
  }

  static initializeLanguage(): void {
    console.log(`[LanguageManager] Initializing language...`);
    const language = this.getLanguage();
    this.setLanguage(language);
  }

  static getCookieValue(name: string): string | null {
    const cookies = document.cookie.split(";");
    const cookie = cookies.find((c) => c.trim().startsWith(`${name}=`));
    return cookie ? cookie.split("=")[1] : null;
  }

  static debugCookies(): void {
    console.log(`[LanguageManager] All cookies:`, document.cookie);
    console.log(
      `[LanguageManager] Our cookie value:`,
      this.getCookieValue(this.COOKIE_NAME)
    );
  }
}
